import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const SENSORY = '#2493A2';
const INTEGRATION = '#57BDA2';
const SYMBOLIC = '#DCB13C';
const CAPSTONE = '#E85D75';

interface Gauge {
  label: string;
  fill: number; // 0-1
  color: string;
  cx: number;
  cy: number;
}

export function StateGaugeCluster() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const height = 180;
    const centerX = width / 2;
    const centerY = height / 2;

    const gauges: Gauge[] = [
      { label: 'tension', fill: 0.7, color: CAPSTONE, cx: 65, cy: 55 },
      { label: 'energy', fill: 0.4, color: SENSORY, cx: 195, cy: 55 },
      { label: 'hunger', fill: 0.85, color: SYMBOLIC, cx: 65, cy: 135 },
      { label: 'alertness', fill: 0.55, color: INTEGRATION, cx: 195, cy: 135 },
    ];

    const gaugeRadius = 30;
    const arcGen = d3.arc<d3.DefaultArcObject>();

    // --- Phase 1: Draw individual gauges ---
    const gaugeGroups = svg
      .selectAll('g.gauge')
      .data(gauges)
      .enter()
      .append('g')
      .attr('class', 'gauge')
      .attr('transform', (d) => `translate(${d.cx}, ${d.cy})`);

    // Background arc (full semicircle)
    gaugeGroups
      .append('path')
      .attr('d', arcGen({
        innerRadius: gaugeRadius - 6,
        outerRadius: gaugeRadius,
        startAngle: -Math.PI / 2,
        endAngle: Math.PI / 2,
      }))
      .attr('fill', 'var(--color-text-muted)')
      .attr('opacity', 0.15);

    // Filled arc (animated)
    gaugeGroups
      .append('path')
      .attr('class', 'gauge-fill')
      .attr('fill', (d) => d.color)
      .attr('opacity', 0.8)
      .attr('d', arcGen({
        innerRadius: gaugeRadius - 6,
        outerRadius: gaugeRadius,
        startAngle: -Math.PI / 2,
        endAngle: -Math.PI / 2,
      }))
      .transition()
      .duration(500)
      .delay((_, i) => i * 100)
      .attrTween('d', (d) => {
        const interpolate = d3.interpolate(-Math.PI / 2, -Math.PI / 2 + Math.PI * d.fill);
        return (t: number) =>
          arcGen({
            innerRadius: gaugeRadius - 6,
            outerRadius: gaugeRadius,
            startAngle: -Math.PI / 2,
            endAngle: interpolate(t),
          }) || '';
      });

    // Gauge labels
    gaugeGroups
      .append('text')
      .attr('y', 18)
      .attr('text-anchor', 'middle')
      .attr('font-size', 8.5)
      .attr('font-weight', 500)
      .attr('class', 'viz-text-muted')
      .text((d) => d.label)
      .attr('opacity', 0)
      .transition()
      .delay((_, i) => 200 + i * 100)
      .duration(300)
      .attr('opacity', 1);

    // Percentage inside gauge
    gaugeGroups
      .append('text')
      .attr('y', 3)
      .attr('text-anchor', 'middle')
      .attr('font-size', 10)
      .attr('font-weight', 700)
      .attr('class', 'viz-text')
      .text((d) => `${Math.round(d.fill * 100)}%`)
      .attr('opacity', 0)
      .transition()
      .delay((_, i) => 300 + i * 100)
      .duration(300)
      .attr('opacity', 0.7);

    // --- Phase 2: Gauges shrink and move center ---
    const t1 = d3.timeout(() => {
      gaugeGroups
        .transition()
        .duration(500)
        .attr('transform', `translate(${centerX}, ${centerY - 10}) scale(0.3)`)
        .attr('opacity', 0.3);
    }, 1600);

    // --- Phase 3: Single label appears ---
    const resultLabel = svg
      .append('text')
      .attr('x', centerX)
      .attr('y', centerY + 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', 22)
      .attr('font-weight', 700)
      .attr('class', 'viz-text')
      .attr('opacity', 0)
      .text('anxious');

    const sublabel = svg
      .append('text')
      .attr('x', centerX)
      .attr('y', centerY + 25)
      .attr('text-anchor', 'middle')
      .attr('font-size', 9)
      .attr('class', 'viz-text-muted')
      .attr('opacity', 0)
      .text('4 signals compressed');

    const t2 = d3.timeout(() => {
      resultLabel
        .transition()
        .duration(400)
        .attr('opacity', 1);

      sublabel
        .transition()
        .duration(400)
        .delay(150)
        .attr('opacity', 0.7);
    }, 2300);

    return () => {
      t1.stop();
      t2.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-heart-pulse" /> Interoception &mdash; Internal State Compression
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 180" />
      <p className="viz-container__caption">Many signals become one label.</p>
    </div>
  );
}
